import { useEffect, useState } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/context/LangContext";

function initialsFrom(name) {
  return String(name || "?")
    .trim()
    .split(/\s+/)
    .map((w) => w[0]?.toUpperCase())
    .join("")
    .slice(0, 2) || "★";
}

const SERVICE_HINT = {
  express: "Fret express",
  normal: "Fret aérien",
  maritime: "Fret maritime",
};

export default function Testimonials() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/testimonials?limit=8")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, []);

  if (items.length === 0) return null;

  const go = (delta) => setIdx((i) => (i + delta + items.length) % items.length);
  const current = items[idx];

  return (
    <section className="bg-gradient-to-b from-[var(--bg-soft)] to-white py-20" data-testid="home-testimonials">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <p className="kicker !text-[var(--secondary)]">Ils nous font confiance</p>
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-3">
              Vraies notes. <span className="text-[var(--accent)]">Vrais clients.</span>
            </h2>
          </div>
          {items.length > 1 && (
            <div className="flex items-center gap-2">
              <button onClick={() => go(-1)} className="w-10 h-10 rounded-full border border-black/10 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors flex items-center justify-center" aria-label="Précédent" data-testid="testimonial-prev">
                <ChevronLeft size={18} />
              </button>
              <button onClick={() => go(1)} className="w-10 h-10 rounded-full border border-black/10 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors flex items-center justify-center" aria-label="Suivant" data-testid="testimonial-next">
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>

        <div className="card-flat p-8 md:p-12 relative" data-testid={`testimonial-${idx}`}>
          <Quote className="absolute top-6 right-6 text-[var(--accent)]/15" size={80} />
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[var(--accent)] to-[var(--secondary)] text-white font-display font-black flex items-center justify-center text-lg flex-shrink-0">
              {initialsFrom(current.rating_author)}
            </div>
            <div>
              <p className="font-bold">{current.rating_author || "Client"}</p>
              <p className="text-xs text-[var(--ink-soft)]">
                {SERVICE_HINT[current.service_type] || "Client Negosino"}
                {current.destination && ` · ${current.destination}`}
              </p>
            </div>
          </div>

          <div className="flex gap-1 mt-5">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} size={20} className={n <= current.rating ? "text-yellow-500 fill-yellow-500" : "text-black/10"} />
            ))}
          </div>

          <p className="mt-6 text-lg md:text-xl leading-relaxed font-medium">
            « {current.rating_comment} »
          </p>

          {items.length > 1 && (
            <div className="flex gap-1.5 mt-8">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`Avis ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "bg-[var(--accent)] w-8" : "bg-black/15 w-4 hover:bg-black/30"}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
